import { EntityType } from 'UI/constants/entityTypes';

import { PRIORITY_MAP } from './components/Filters/Filters.constants';
import { createFiltersRequestPayload } from './components/Filters/Filters.utils';

const extractListValue = item => {
  if (typeof item === 'string') return item;
  if (typeof item === 'number') return String(item);
  return item?.value ?? item?.title ?? item?.label ?? item?.name ?? null;
};

const normalizeListValues = (items, extractValue = extractListValue) =>
  Array.isArray(items)
    ? items.flatMap(item => {
        const value = extractValue(item);
        return value ? [value] : [];
      })
    : [];

const extractTitleOrValue = item => item?.title || extractListValue(item);

const normalizeSummaryText = value => (typeof value === 'string' ? value.trim() : '');

const normalizeResumeDate = value => {
  if (typeof value !== 'string') return null;

  const match = value.trim().match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?(?:T.*)?$/);
  if (!match) return null;

  const [, rawYear, rawMonth, rawDay] = match;
  const year = Number(rawYear);
  const month = Number(rawMonth);
  const day = rawDay ? Number(rawDay) : 1;
  const date = new Date(Date.UTC(year, month - 1, day));
  const isValid =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;

  return isValid ? { month, year } : null;
};

const normalizeDatasheetExperiences = experiences => {
  if (!Array.isArray(experiences)) return [];

  return experiences
    .map(experience => {
      const title = normalizeSummaryText(experience?.companyTitle?.title);
      const company = normalizeSummaryText(experience?.companyTitle?.company);
      const location = normalizeSummaryText(experience?.location);
      const startDate = normalizeResumeDate(experience?.fromTo?.from);
      const endDate = normalizeResumeDate(experience?.fromTo?.to);

      return {
        title,
        company,
        location,
        start_date: startDate,
        end_date: endDate,
        is_current: Boolean(startDate && !endDate)
      };
    })
    .filter(({ title, company, location, start_date: startDate, end_date: endDate }) =>
      Boolean(title || company || location || startDate || endDate)
    );
};

const normalizeBasics = (resumeBasics = {}, topBasics = {}, topLevelUrls = []) => {
  const emails = (() => {
    if (resumeBasics.emails?.length) return resumeBasics.emails;
    if (topBasics.email) return [topBasics.email];
    return [];
  })();

  const phoneDetails = (() => {
    if (resumeBasics.phone_numbers?.length) {
      return resumeBasics.phone_numbers
        .map(number => ({ number, use: null }))
        .filter(p => p.number);
    }
    if (topBasics.phones?.length) {
      return topBasics.phones
        .map(p => ({ number: p?.fullNumber || p?.phoneNumber, use: p?.use || null }))
        .filter(p => p.number);
    }
    return [];
  })();
  const phoneNumbers = phoneDetails.map(p => p.number);

  const urls = (() => {
    if (resumeBasics.urls?.length) return resumeBasics.urls;
    if (topLevelUrls?.length) return topLevelUrls;
    return [];
  })();

  const basicsLocation = topBasics.location;

  const basicsAddress = {
    found_match: Boolean(basicsLocation?.city || basicsLocation?.zip),
    city_title: basicsLocation?.city?.title || basicsLocation?.city,
    state_slug: basicsLocation?.state?.slug,
    zip_title: basicsLocation?.zip,
    country_slug: basicsLocation?.country?.slug
  };

  const resumeAddress = resumeBasics.address_components || {};
  const hasResumeLocation = Boolean(resumeAddress.city_title || resumeAddress.zip_title);

  const addressParts = (() => {
    if (basicsLocation?.zip) return { computed: basicsLocation.zip };
    if (basicsLocation?.city) {
      const cityTitle = basicsLocation.city?.title || basicsLocation.city;
      const suffix = basicsLocation?.state?.slug ? `, ${basicsLocation.state.slug}` : '';
      return { computed: `${cityTitle}${suffix}` };
    }
    if (resumeBasics.address) return { computed: resumeBasics.address };
    return { computed: '' };
  })();

  const addressComponents = (() => {
    // Basics wins only when it actually resolved a city; a zip-only basics should not
    // bury a parsed-resume city/state, since the card renders city/state/zip.
    if (basicsAddress.city_title) return basicsAddress;
    if (hasResumeLocation) return resumeAddress;
    return basicsAddress;
  })();

  return {
    ...resumeBasics,
    full_name: topBasics.fullName || '',
    first_name: topBasics.firstName || resumeBasics.first_name || '',
    last_name: topBasics.lastName || resumeBasics.last_name || '',
    emails,
    phone_numbers: phoneNumbers,
    phone_details: phoneDetails,
    urls,
    address: addressParts.computed,
    address_components: addressComponents
  };
};

export const normalizeResumeSearchItem = item => {
  if (!item) return null;
  const resumeData =
    Array.isArray(item.resumes) && item.resumes.length > 0
      ? item.resumes[item.resumes.length - 1]
      : {};
  const resumeBasics = resumeData.basics || {};
  const topBasics = item.basics || {};

  const basics = normalizeBasics(resumeBasics, topBasics, item.urls);

  const skills = normalizeListValues(resumeData.skills);

  const experiences = resumeData.professional_experiences?.length
    ? resumeData.professional_experiences
    : normalizeDatasheetExperiences(
        item.datasheet?.data?.candidateBackground?.employmentHistoryItems
      );

  const educations = resumeData.educations?.length
    ? resumeData.educations
    : item.datasheet?.data?.candidateBackground?.educationItems ?? [];

  const certifications = resumeData.certifications_and_licenses?.length
    ? resumeData.certifications_and_licenses
    : item.datasheet?.data?.candidateBackground?.licensesAndCertificationsItems ?? [];

  const associations = resumeData.professional_associations_and_memberships || [];

  const qualification = item.datasheet?.data?.qualification || {};
  const rawLastActivity = item.lastActivity;
  const lastActivity = rawLastActivity
    ? {
        date: rawLastActivity.date || null,
        type:
          typeof rawLastActivity.type === 'string'
            ? rawLastActivity.type
            : rawLastActivity.type?.title || null,
        recruiter: rawLastActivity.recruiter
          ? {
              fullName: rawLastActivity.recruiter.fullName || null,
              id: rawLastActivity.recruiter.id ?? null
            }
          : null
      }
    : null;
  const rawUrgency = item.datasheet?.data?.establishingUrgency;
  const urgency = rawUrgency
    ? {
        idealTimeToStart: extractTitleOrValue(rawUrgency.idealTimeToStart) || null
      }
    : null;
  const rawWorkPreferences = item.datasheet?.data?.workPreferences;
  const workPreferences = rawWorkPreferences
    ? {
        salaryRange: rawWorkPreferences.salaryRange || rawWorkPreferences.salaryRanges || null,
        workPreference: extractTitleOrValue(rawWorkPreferences.workPreference) || null,
        relocation:
          extractTitleOrValue(rawWorkPreferences.relocation?.areYouWillingToRelocate) || null
      }
    : null;

  const normalizeCandidateField = obj => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (typeof obj === 'object') return obj;
    return null;
  };

  return {
    candidate: {
      id: item.id,
      type: qualification.candidateType
        ? { id: qualification.candidateType.id, title: qualification.candidateType.title }
        : null,
      status: qualification.candidateStatus
        ? { id: qualification.candidateStatus.id, title: qualification.candidateStatus.title }
        : null,
      functionalTitle: normalizeCandidateField(topBasics.functionalTitle),
      industry: normalizeCandidateField(topBasics.industry),
      specialty: normalizeCandidateField(topBasics.specialty),
      subspecialty: normalizeCandidateField(topBasics.subspecialty),
      last_activity_date: item.lastActivity?.date || null,
      currentCompany: topBasics.currentCompany?.title || null,
      workPreferences,
      urgency,
      lastActivity,
      teamName: item.team?.team?.fullName || '',
      emailStatus: topBasics.emailStatus || null
    },
    profile: {
      basics,
      professional_experiences: experiences,
      educations,
      certifications_and_licenses: certifications,
      professional_associations_and_memberships: associations,
      skills
    },
    meta: {
      file: {
        uploadedAt: item.createdAt || item.updatedAt || null
      }
    }
  };
};

export const normalizeResumeSearchResponse = response => {
  const rawItems = response?.data?.data ?? response?.data ?? [];
  const items = Array.isArray(rawItems) ? rawItems : [];
  const total = response?.data?.total ?? response?.total ?? 0;
  return {
    data: items.map(normalizeResumeSearchItem).filter(Boolean),
    total
  };
};

export const buildSearchProjectQueryParams = ({ filtersFormValues }) => {
  const searchParamsKey = `${EntityType.Candidate}_query`;
  const { filters } = createFiltersRequestPayload(filtersFormValues, PRIORITY_MAP);

  return {
    params: { filters },
    type: EntityType.Candidate,
    searchParamsKey
  };
};
